<script lang="ts">
	//Formatters
	import { format } from 'src/services/i18n';
	//Stores
	import { user_data } from 'src/stores/account/user';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { rooms } from 'src/stores/lists/rooms';
	//Design system components
	import { Text, IconButton, Button, toast } from '@Shelly/shelly-design-system';
	import DeviceInfoWrapper from './DeviceInfoWrapper.svelte';
	//Types
	import type { DeviceUsage } from './device_usage_helper';
	import type { Device } from 'src/types/Device';
	//Helpers
	import { isWallDisplay, is_loqed } from 'src/helpers/interface_helpers';
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		getAlarmsWithDevice,
		getGroupsWithDevice,
		getScenesWithDevice,
		getThermostatsWithDevice,
		getDashboardsWithDevice,
	} from './device_usage_helper';
	import { copy } from 'src/helpers/copy_text';
	import { update } from 'src/stores/ui/edit_lists';
	import { profiles } from 'src/stores/features/profiles';
	import { unitOptions } from 'src/stores/account/home';
	import BatchTransferModal from 'src/components/BatchCommandModals/BatchTransferModal.svelte';
	import { getDeviceInfoEntries } from './device_info_helper';
	//Components
	import DeleteModal from './DeleteDeviceModal.svelte';
	import SaveTemplateModal from 'src/components/Templates/SaveTemplateModal.svelte';
	import { isActiveHomeShared } from 'src/helpers/houses/homes_helpers';
	import { homes } from 'src/stores/account/home';
	import SharedHomeOwnerManagedAlert from 'src/components/SharedHome/SharedHomeOwnerManagedAlert.svelte';

	export let device: Device;
	export let showEmbeddedTransferDelete = true;
	export let useNewLayout = false;

	$: isSharedHomeActive = isActiveHomeShared($homes.list);

	let deleteLoading = false;
	let showModalDelete = false;
	let showTransferModal = false;
	let showSaveConfigModal = false;

	let deviceUsage = {
		room: [],
		group: [],
		alarm: [],
		scene: [],
		dashboard: [],
		thermostat: [],
	} as DeviceUsage;

	// Flag if device is used in any of the categories
	let deviceIsUsed: boolean = false;

	$: statusStore = getDeviceStatus(device?.id);
	$: settingsStore = getDeviceSettings(device?.id);

	// Data for the collapse section
	$: deviceInfoEntries = getDeviceInfoEntries(device, $statusStore, $settingsStore);

	const onTransferButtonClick = () => {
		if ($profiles.length > 1 || $unitOptions.length > 0) {
			update.update(() => ({
				value: true,
				subType: '',
				selectAll: false,
				type: 'account_transfer_devices',
				selectedDevices: { [device.id]: device } as any,
			}));
			showTransferModal = true;
		} else {
			toast.open({
				type: 'info',
				title: $format('alert_no_switchable_accounts_or_other_homes', {
					default: 'You have no switchable accounts or other homes to transfer your devices.',
				}),
			});
		}
	};

	const onDeleteButtonClick = () => {
		// Lists with device usage
		deviceUsage = {
			room: $rooms[device.room_id] !== undefined ? [$rooms[device.room_id]] : [],
			group: getGroupsWithDevice(device.id),
			alarm: getAlarmsWithDevice(device.id),
			scene: getScenesWithDevice(device.id),
			dashboard: getDashboardsWithDevice(device.id),
			thermostat: getThermostatsWithDevice(device.id),
		} as DeviceUsage;

		// Flag if device is used in any of the categories
		deviceIsUsed = Object.values(deviceUsage).some((array) => array.length > 0);

		showModalDelete = true;
	};

	$: buttonTitle = useNewLayout
		? capFirstChar($format('sub_settings_title_transfer_device', { default: 'transfer device' }))
		: $format('button_transfer', { default: 'Transfer' });

	$: deleteLabel = getDeleteLabel(useNewLayout, device.shared);

	function getDeleteLabel(isNewLayout: boolean, isShared: boolean): string {
		if (isShared) {
			return isNewLayout
				? capFirstChar($format('button_remove_device', { default: 'Remove device' }))
				: $format('remove_from_my_acc', { default: 'Remove from my account' });
		}
		return isNewLayout
			? capFirstChar($format('sub_settings_title_delete_device', { default: 'delete device' }))
			: $format('button_delete', { default: 'Delete' });
	}
</script>

<DeviceInfoWrapper useContainer={useNewLayout}>
	{#each deviceInfoEntries as data, i}
		<div class="d-flex flex-wrap justify-content-between align-items-center min-height-32">
			<Text
				styleType={'body-small'}
				class={`text-bold align-self-center mr-s text-capitalize ${!data.copyButton ? 'mb-xs ' : ''}`}
				>{data.title}</Text
			>
			<div class="d-flex flex-auto justify-content-between align-items-center">
				<Text
					styleType={'body-small'}
					class={`text-break-all ${data.isLink ? 'link-styles text-small' : ''} ${!data.copyButton ? 'mb-xs ' : ''}`}
					on:click={() => {
						if (data.isLink && data.linkClick) {
							data.linkClick();
						}
					}}>{data.desc}</Text
				>
				{#if data.copyButton}
					<IconButton
						icon="fa-solid fa-copy"
						variant={useNewLayout ? 'secondary' : 'primary'}
						class="ml-s"
						on:click={() =>
							copy(
								data.desc,
								$format('copied_successfully', {
									default: 'Copied successfully!',
								}),
							)}
						aria-label={$format('button_copy', { default: 'Copy' })}
					/>
				{/if}
				{#if data.saveConfiguration && $statusStore?.online}
					<IconButton
						icon="fas fa-file-alt"
						on:click={() => (showSaveConfigModal = true)}
						aria-label={$format('save_template_tooltip', {
							default: 'Save device configuration as template',
						})}
						tooltip={$format('save_template_tooltip', {
							default: 'Save device configuration as template',
						})}
					/>{/if}
			</div>
		</div>
		{#if i !== deviceInfoEntries.length - 1}
			<hr class="mt-s mb-s" />
		{/if}
	{/each}
</DeviceInfoWrapper>

{#if showEmbeddedTransferDelete}
	<div class={useNewLayout ? 'mt-m' : 'mt-s'}>
		{#if isSharedHomeActive}
			<SharedHomeOwnerManagedAlert class="mb-s" context="generic" />
		{/if}
		<!-- Transfer button  -->
		{#if !device.shared && !device.shared_by_me && !device.id.includes('_') && !isWallDisplay(device.type) && !is_loqed(device) && !$user_data?.installer}
			<Button
				class="width-sm-full mb-s"
				variant={useNewLayout ? 'secondary' : 'primary'}
				size="m"
				responsiveSize={{
					sm: 'm',
				}}
				loading={deleteLoading}
				disabled={isSharedHomeActive}
				on:click={onTransferButtonClick}
				aria-label={buttonTitle}
			>
				{buttonTitle}
			</Button>
		{/if}
		<!-- Transfer button -->

		<!-- Delete button  -->
		<Button
			class="width-sm-full"
			variant="danger"
			size="m"
			responsiveSize={{
				sm: 'm',
			}}
			loading={deleteLoading}
			disabled={isSharedHomeActive}
			on:click={onDeleteButtonClick}
			aria-label={deleteLabel}
		>
			{deleteLabel}
		</Button>
		<!-- Delete button -->
	</div>
{/if}

{#if showSaveConfigModal}
	<SaveTemplateModal bind:showModal={showSaveConfigModal} {device} />
{/if}

{#if showEmbeddedTransferDelete && !isSharedHomeActive}
	<!-- Transfer modal -->
	{#if showTransferModal}
		<BatchTransferModal bind:showModal={showTransferModal} areFiltersDisabled={false} />
	{/if}
	<!-- Transfer modal -->

	<!-- Delete modal -->
	<DeleteModal
		bind:showModalDelete
		{device}
		{deviceIsUsed}
		{deviceUsage}
		on:close={() => {
			showModalDelete = false;
		}}
		on:delete={(e) => {
			deleteLoading = e.detail.deleteLoading;
		}}
	/>
	<!-- Delete modal -->
{/if}
